import type { IRecord, ISearchPayloadPageStore } from '@gp/types/global';
import type { ISelectOptionWithGroup } from '@intelligence/types/common';
import type { PagingQuery } from 'tea-component';

export const BROWSER_LANGUAGE = (() => {
  let locale = '';
  try {
    locale = Intl.NumberFormat().resolvedOptions().locale;
  } catch (exception) {
    console.warn(exception);
  }
  return locale || navigator.language || '';
})();

/**
 * 将对象的键与值互换
 * @param obj 传进来的对象
 * @returns 翻转后的对象
 */
export const getReverseObj = (obj: { [keyName: string]: any; }) => {
  if (Object.prototype.toString.call(obj) !== '[object Object]') return obj;
  const reverseObj: { [keyName: string]: string; } = {};
  for (const key in obj) {
    reverseObj[String(obj[key])] = key;
  }
  return reverseObj;
};

export const G_POP = 'g-pop';
export const SLASH_G_POP = `/${G_POP}`;

export const SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX = 352;
export const SELECT_PANEL_HEIGHT = 300;

// 在线权限申请入口链接
export const PERMISSION_APPLY_URL =
  'https://doc.weixin.qq.com/forms/AJEAIQdfAAoABQA3AY2AD4SXkYNM0ZTXf';

// getTextWithHighlight方法 高亮关键字的特性配置
export const GET_TEXT_WITH_HIGHLIGHT_OPTION = {
  // 是否开启字符拆分模式，即拆分为单个字符进行匹配关键字
  isSeparate: false,
  // 是否忽略大小写
  isIgnoreCase: true,
  className: 'dbg-highlight-normal-text',
};

// 常用字符串key常量
export const CONSTANT_KEYS = {
  FULL_YEAR: 'full_year',
  YTM: 'ytm',
  ACTUAL: 'actual',
  BUDGET: 'budget',
  FORECAST: 'forecast',
  '1ST_PARTY': '1st party',
  '2ND_PARTY': '2nd party',
  '3RD_PARTY': '3rd party',
};

// 常用文案（仅展示）
export const CONSTANT_TEXTS = {
  ADD: 'Add',
  EDIT: 'Edit',
  DELETE: 'Delete',
  ACTIONS: 'Actions',
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  ON_HOLD: 'On hold',
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
  EMPTY_TEXT: 'No results found',
  EMPTY_TEXT1: 'No result found.',
  SORRY_EMPTY_TEXT: 'Sorry! No results found.',
  OPERATION_SUCCESS_TIPS: 'Operation successful',
  OPERATION_FAILURE_TIPS: 'Operation failure, please try again.',
  OFFICIAL_GATE_REVIEW: 'Official Gate Review',
  APPROVED_DEVELOPMENT_BUDGET_SIMPLE: 'Approved Dev Budget',
  APPROVED_DEVELOPMENT_BUDGET: 'Approved Development Budget',
  FULL_YEAR: 'Full Year',
  YTM: 'YTM',
  SELECT_SEPARATED_GROUP_NAME: 'Filter',
  ACTUAL: 'Actual',
  BUDGET: 'Budget',
  FORECAST: 'Forecast',
  YES: 'Yes',
  NO: 'No',
  MILESTONE: 'Milestone',
  MILESTONE_TIME: 'Review Date',
  '1ST_PARTY': '1st Party',
  '2ND_PARTY': '2nd Party',
  '3RD_PARTY': '3rd Party',
  GR_ACTION_ITEMS: 'GR Action Items',
  DEFINITION: 'Definition',
  DATA_SOURCE: 'Data Source',
  UPDATE_FREQUENCY: 'Update Frequency',
  DATA_TIME: 'Data Time',
};

export const PROJECT_GROUP_NAMES: IRecord<string> = {
  [CONSTANT_KEYS['1ST_PARTY']]: CONSTANT_TEXTS['1ST_PARTY'],
  [CONSTANT_KEYS['2ND_PARTY']]: CONSTANT_TEXTS['2ND_PARTY'],
  [CONSTANT_KEYS['3RD_PARTY']]: CONSTANT_TEXTS['3RD_PARTY'],
};

export const PLATFORM_NAMES: IRecord<string> = {
  mobile: 'Mobile',
  pc: 'PC',
  console: 'Console',
  'ps4/pro': 'PS4/Pro',
  'xbox one': 'Xbox One',
  'xbox series x/s': 'Xbox Series X/S',
  ps5: 'PS5',
  switch2: 'Switch2',
  switch1: 'Switch1',
};

export const TEAM_NAMES: IRecord<string> = {
  studio: 'Studio',
  publisher: 'Publisher',
  fbi: 'FBI',
};

// 系统新手引导配置
const NEW_USERS_GUIDE: any[] = [
  {
    element: '.databrain__header .hmi-active',
    placement: 'bottom',
    title: 'G-POP',
    description:
      'Information presentation and workflow processing of online business management, strategy, Pipeline, studio management, finance, etc.',
  },
  {
    element: '.gp-left__menu .Pipeline .tea-menu__item',
    title: 'Pipeline',
    description:
      'IEG Global production line management, providing overseas product layout information and progress management, including product information, R&D progress, etc.',
  },
  {
    element: '.gp-left__menu .Pipeline .Dashboard',
    title: 'Pipeline-Overview',
    description:
      "Dashboard, the summary information of IEG Global's in-development projects, including the number, investment and key milestone information.",
  },
  {
    element: '.gp-left__menu .Pipeline .All-Projects',
    title: 'Pipeline-Search Project',
    description:
      'All projects, present key information of all in-development projects, you can also search the  project here.',
  },
  {
    element: '.gp-left__menu .Globalpedia',
    title: 'Globalpedia',
    description:
      'Overseas game product tuning Benchmark reference system. Fill in the target game area, category and key indicator data to obtain the game evaluation results.',
  },
  {
    element: '.gp-left__menu .My-Workspace .tea-menu__item',
    title: 'My Workspace',
    description: 'My Workspace, add a new product, and manage product review.',
  },
];
// 系统引导及页面引导对应接口映射规则（前端引导完成给后端传什么字段，查询时也是传什么字段）
const G_POP_SYSTEM = 'g_pop-system'; // 这个字段如果改了的话，情报系统也需要改，对应上（当gp系统展示新手引导时，version history弹窗将不需要展示）
const G_POP_PAGE_DASHBOARD = 'g_pop-page-dashboard';
const G_POP_PAGE_ALL_PROJECTS_PROJECT_LIST =
  'g_pop-page-all_projects-project_list';
const G_POP_PAGE_ALL_PROJECTS_TIMELINE = 'g_pop-page-all_projects-timeline';
const G_POP_PAGE_PRODUCT_REVIEW = 'g_pop-page-product_review';
const G_POP_PAGE_PROJECT_UPDATE_FULL_ACTION_ITEMS_LIST =
  'g_pop-page-project_update-full_action_items_list';
const G_POP_GUIDE_MAP = {
  G_POP_SYSTEM,
  G_POP_PAGE_DASHBOARD,
  G_POP_PAGE_ALL_PROJECTS_PROJECT_LIST,
  G_POP_PAGE_ALL_PROJECTS_TIMELINE,
  G_POP_PAGE_PRODUCT_REVIEW,
  G_POP_PAGE_PROJECT_UPDATE_FULL_ACTION_ITEMS_LIST,
};

export {
  G_POP_GUIDE_MAP,
  G_POP_PAGE_ALL_PROJECTS_PROJECT_LIST,
  G_POP_PAGE_ALL_PROJECTS_TIMELINE,
  G_POP_PAGE_DASHBOARD,
  G_POP_PAGE_PRODUCT_REVIEW,
  G_POP_PAGE_PROJECT_UPDATE_FULL_ACTION_ITEMS_LIST,
  G_POP_SYSTEM,
  NEW_USERS_GUIDE,
};

// 菜单栏每项对应id，对应后端接口id，页面选中展示及页面刷新对应选中项保持高亮使用
export const MENU_ID_MAP: { [keyName: string]: string; } = {
  globalpedia_benchmark: '116',
  pipeline_dashboard: '110',
  'pipeline_all-projects': '111',
  'studio-dashboard_studio-overall': '141',
  'my-workspace_add-new-product': '113',
  'my-workspace_product-review': '114',
  newsletter_report: '122',
  'newsletter_push-management': '123',
};

const MENU_CODE_STUDIO_MONTHLY_REPORT = 'studio_monthly_report';
const MENU_CODE_PERFORMANCE_OVERVIEW = 'performance_overview';
const MENU_CODE_GROSS_REVENUE_TRENDS = 'gross_revenue_trends';
const MENU_CODE_LIVE_TITLE = 'live_title';
const MENU_CODE_IN_DEVELOPMENT = 'in_development';
const MENU_CODE_GLOBALPEDIA = 'globalpedia';
const MENU_CODE_BENCHMARK = 'benchmark';
const MENU_CODE_PIPELINE = 'pipeline';
const MENU_CODE_OVERVIEW = 'pipeline_overview';
const MENU_CODE_PROJECT_LIST = 'pipeline_project';
const MENU_CODE_PIPELINE_GR_ACTION_ITEMS = 'pipeline_gr_action_items';
const MENU_CODE_STUDIO_DASHBOARD = 'studio_dashboard';
const MENU_CODE_STUDIO_STUDIO_BUSINESS_METRICS = 'studio_business_metrics';
const MENU_CODE_STUDIO_OVERALL = 'studio_overall';
const MENU_CODE_MY_WORKSPACE = 'workspace';
const MENU_CODE_METRICS = 'metrics';
const MENU_CODE_ADD_NEW_PRODUCT = 'add_new_product';
const MENU_CODE_DPS_WEEKLY_REPORT = 'dps_weekly_report';
const MENU_CODE_GATE_REVIEW = 'gate_review';
const MENU_CODE_NEWSLETTER = 'newsletter';
const MENU_CODE_REPORT = 'report';
const MENU_CODE_REPORT_PUSH_MANAGEMENT = 'push_management';
const MENU_CODE_CHANGE_LOG = 'pipeline_change_log';
const MENU_CODE_FAVORITE_PROJECT = 'favorite_project';
const MENU_CODE_USER_GUIDE = 'user_guide';
const MENU_CODE_VERSION_HISTORY = 'version_history';

export {
  MENU_CODE_ADD_NEW_PRODUCT,
  MENU_CODE_BENCHMARK,
  MENU_CODE_CHANGE_LOG,
  MENU_CODE_DPS_WEEKLY_REPORT,
  MENU_CODE_GATE_REVIEW,
  MENU_CODE_GLOBALPEDIA,
  MENU_CODE_GROSS_REVENUE_TRENDS,
  MENU_CODE_IN_DEVELOPMENT,
  MENU_CODE_LIVE_TITLE,
  MENU_CODE_METRICS,
  MENU_CODE_MY_WORKSPACE,
  MENU_CODE_NEWSLETTER,
  MENU_CODE_OVERVIEW,
  MENU_CODE_PERFORMANCE_OVERVIEW,
  MENU_CODE_PIPELINE,
  MENU_CODE_PIPELINE_GR_ACTION_ITEMS,
  MENU_CODE_PROJECT_LIST,
  MENU_CODE_REPORT,
  MENU_CODE_REPORT_PUSH_MANAGEMENT,
  MENU_CODE_STUDIO_DASHBOARD,
  MENU_CODE_STUDIO_MONTHLY_REPORT,
  MENU_CODE_STUDIO_OVERALL,
  MENU_CODE_STUDIO_STUDIO_BUSINESS_METRICS,
  MENU_CODE_FAVORITE_PROJECT,
  MENU_CODE_USER_GUIDE,
  MENU_CODE_VERSION_HISTORY,
};

// 所有echarts图表的颜色配置
export const ECHART_COLOR = [
  '#5086F3',
  '#77E097',
  '#6A7597',
  '#F8CC6A',
  '#A08FF2',
  '#5EC8F1',
  '#CF61AC',
  '#F19E55',
  '#387F7D',
  '#F17C86',
  '#8EB9FF',
];

// 项目进度枚举值
export const PROGRESS_ENUM: { [keyName: number]: string; } = {
  1: 'On Track',
  2: 'At Risk',
  3: 'Monitor',
  4: 'On Hold',
  5: 'Not Started',
};
// 项目进度枚举值-stats筛选器专用
export const PROGRESS_ENUM_OPTIONS: { [keyName: number]: string; } = {
  1: 'On Track',
  2: 'At Risk',
  3: 'Monitor',
  4: 'On Hold',
  5: 'Not Started',
  0: 'Others',
};
export const PROGRESS_REFLECT = getReverseObj(PROGRESS_ENUM_OPTIONS);
// 项目进度对应字体颜色及标签背景颜色，表格中使用
export const PROGRESS_TEXT_COLOR: any = {
  '': {
    color: '#515151',
    bgColor: 'rgba(81, 81, 81, 0.1)',
  },
  'On Track': {
    color: '#326FF0',
    bgColor: 'rgba(50, 111, 240, 0.12)',
  },
  'At Risk': {
    color: '#EF5858',
    bgColor: 'rgba(239, 88, 88, 0.12)',
  },
  Monitor: {
    color: '#EDA53A',
    bgColor: 'rgba(237, 165, 58, 0.12)',
  },
  'On Hold': {
    color: '#6A6D7E',
    bgColor: 'rgba(106, 109, 126, 0.12)',
  },
  'Not Started': {
    color: '#6A6D7E',
    bgColor: 'rgba(50, 111, 240, 0.12)',
  },
};

// 项目进度对应字体颜色及标签背景颜色，黑色背景气泡使用
export const PROGRESS_TEXT_COLOR_BG_BLACK: any = {
  '': {
    color: '#fff',
    bgColor: 'rgba(255, 255, 255, 0.2)',
  },
  'On Track': {
    color: '#fff',
    bgColor: 'rgba(0, 118, 255, 0.2)',
  },
  'At Risk': {
    color: '#E54545',
    bgColor: 'rgba(229, 69, 69, 0.2)',
  },
  Monitor: {
    color: '#FF9D00',
    bgColor: 'rgba(225, 157, 0, 0.2)',
  },
  'On Hold': {
    color: '#fff',
    bgColor: 'rgba(255, 255, 255, 0.2)',
  },
  'Not Started': {
    color: '#fff',
    bgColor: 'rgba(0, 118, 255, 0.2)',
  },
};

// 项目进度对应字体颜色及标签背景颜色，详情页头部标签使用
export const PROGRESS_TEXT_COLOR_FOR_DEATIL_HEADER: any = {
  '': {
    color: '#525464',
    bgColor: 'rgba(34, 34, 34, 0.1)',
  },
  'On Track': {
    color: '#326FF0',
    bgColor: 'rgba(50, 111, 240, 0.12)',
  },
  'At Risk': {
    color: '#EF5858',
    bgColor: 'rgba(239, 88, 88, 0.12)',
  },
  Monitor: {
    color: '#EDA53A',
    bgColor: 'rgba(237, 165, 58, 0.12)',
  },
  'On Hold': {
    color: '#6A6D7E',
    bgColor: 'rgba(106, 109, 126, 0.12)',
  },
  'Not Started': {
    color: '#6A6D7E',
    bgColor: 'rgba(50, 111, 240, 0.12)',
  },
};

// 优先权图标颜色
export const PRIORITY_COLOR = {
  1: '#F17C86',
  2: '#F5D242',
  3: '#B6D1FB',
  4: '#D8DAE4',
  5: '#F19E55',
  11: '#F17C86',
  22: '#F19E55',
  33: '#F5D242',
  44: '#B6D1FB',
  99: '#D8DAE4',
};
export const STATUS_MAP = [
  'On Track',
  'At Risk',
  'Monitor',
  'On Hold',
  'Not Started',
];
export const PRIORITY_MAP = ['S', 'A', 'B', 'C', 'Z'];
export const IP_MAP = ['Existing', 'New', 'Licensed'];

// 处理Others的空值
export const OHERSKEY = 'Others';

// 工作台Stage对应字段
export const STAGE_MAP: { [keyName: number]: string; } = {
  1: 'Materials',
  2: 'Evaluation',
  3: 'Internal Approval',
  4: 'Summary',
  5: 'Final Approval',
  6: 'Finished',
};

// 项目Stage 对应字段
export const STAGE_MAP_OF_PROJECT: { [key: number]: string; } = {
  1: 'Materials',
  2: 'Evaluation',
  3: 'Internal Approval',
  4: 'Summary',
  5: 'Final Approval',
};

// 这里默认值给后端，没有权限的列表接口不会返回，所以默认值不用做处理
// 其次列表stage字段传给后端的值确保单词一致就行，大小写不用处理，后端会统一转成小写去拿数据
// export const DEFAULT_STAGE_VALUE = [
//   'Live',
//   'Development',
//   'Legacy',
//   'Pre-planned',
//   'Planned' /* 'Canceled', 'No maintenance' */,
// ];

// 配合接口转为小写
export const DEFAULT_STAGE_VALUE_OVERVIEW = [
  'live',
  'development',
  'pre-planned',
  'planned' /* 'Canceled', 'No maintenance' */,
];

// Push Management列表Push Status列映射表
export const PUSH_STATUS_MAP: {
  [keyName: number]: { text: string; color: string; };
} = {
  0: {
    text: 'Pending push', // 未推送
    color: '#888',
  },
  1: {
    text: 'Successful push', // 推送成功
    color: '#0ABF5B',
  },
  2: {
    text: 'Push failure', // 推送失败
    color: '#E54545',
  },
  3: {
    text: 'Partial success', // 部分推送成功
    color: '#FF9D00',
  },
  4: {
    text: 'Pending push', // 推送中
    color: '#888',
  },
  5: {
    text: 'Pending push', // 队列中
    color: '#888',
  },
};

// 公用转圈圈的loading icon
export const SpinLoadingIcon =
  'data:image/png;base64,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';

// 企业微信链接
export const ENTERPRISE_WECHAT_LINK = 'https://doc.weixin.qq.com/';

// 默认表格分页大小
export const DEFAULT_PAGE_SIZE = 100;

// 表格分页大小列表
export const PAGE_SIZES = [20, 50, DEFAULT_PAGE_SIZE];

// 表格出现分页器的最少数据条数
export const MIN_RECORDS_COUNT = PAGE_SIZES[0];

// 下载文件类型常量
export const FILE_TYPES_MAP = {
  CSV: '1',
  EXCEL: '2',
  PDF: '3',
};

// 下载文件格式1、PDF、CSV
export const FILE_TYPES1 = [
  { text: 'PDF', value: FILE_TYPES_MAP.PDF },
  { text: 'CSV', value: FILE_TYPES_MAP.CSV },
];

// 下载文件格式1、EXCEL、CSV
export const FILE_TYPES2 = [
  { text: 'EXCEL', value: FILE_TYPES_MAP.EXCEL },
  { text: 'CSV', value: FILE_TYPES_MAP.CSV },
];

// 组件内部对text有做翻译处理，这里text要走国际化翻译的形式
export const SIMPLE_DOWNLOAD_FILE_TYPES1 = [
  { text: 'dbg_download_excel', value: FILE_TYPES_MAP.EXCEL },
  { text: 'dbg_download_csv', value: FILE_TYPES_MAP.CSV },
];

export const GUIDELINES_LINK =
  'https://doc.weixin.qq.com/doc/w3_AA4AMwZ2ACcGF41BXPOTCy6JibO1X?scode=AJEAIQdfAAoBEL6oUHAGgALgaWAEw';

// 常用图片格式
export const COMMON_IMAGE_SUFFIXES = ['jpg', 'jpeg', 'png', 'bmp', 'webp'];

export const COMMON_IMAGE_ACCEPTS = COMMON_IMAGE_SUFFIXES.map(
  item => `.${item}`,
);

// 图片文件后缀
export const IMAGE_SUFFIXES = [
  ...COMMON_IMAGE_SUFFIXES,
  'pjpeg',
  'gif',
  'svg',
  'svgz',
  'xbm',
  'tif',
  'pjp',
  'ico',
  'tiff',
  'jfif',
  'avif',
];

// 视频文件后缀
export const VIDEO_SUFFIXES = [
  'avi',
  'wmv',
  'mpeg',
  'mp4',
  'm4v',
  'mov',
  'asf',
  'flv',
  'f4v',
  'rmvb',
  'rm',
  'mkv',
];
// export const VIDEO_SUFFIXES = ['avi', 'wmv', 'mpeg', 'mp4', 'm4v', 'mov', 'asf', 'flv', 'f4v', 'rmvb', 'rm'];

// 演示文件类型，PPT类型文件后缀
const PRESENTENTION_FILE_SUFFIXES = [
  'ppt',
  'pptx',
  'pot',
  'potx',
  'pps',
  'ppsx',
  'dps',
  'dpt',
  'pptm',
  'potm',
  'ppsm',
];

// 文字文件类型，WORLD类型文件后缀
const WORLD_FILE_SUFFIXES = [
  'doc',
  'dot',
  'wps',
  'wpt',
  'docx',
  'dotx',
  'docm',
  'dotm',
];

// 表格文件类型，EXCEL、CSV类型文件后缀
const EXCEL_FILE_SUFFIXES = [
  'xls',
  'xlt',
  'et',
  'ett',
  'xlsx',
  'xltx',
  'csv',
  'xlsb',
  'xlsm',
  'xltm',
  'ets',
];

// 其他类型文件后缀
const OTHER_FILE_SUFFIXES = [
  'pdf',
  'lrc',
  'c',
  'cpp',
  'h',
  'asm',
  's',
  'java',
  'asp',
  'bat',
  'bas',
  'prg',
  'cmd',
  'rtf',
  'txt',
  'log',
  'xml',
  'htm',
  'html',
];

// 腾讯云COS文档预览支持的文件类型后缀
export const COS_PREVIEWABLE_FILE_SUFFIXES = [
  ...PRESENTENTION_FILE_SUFFIXES,
  ...WORLD_FILE_SUFFIXES,
  ...EXCEL_FILE_SUFFIXES,
  ...OTHER_FILE_SUFFIXES,
];

// 腾讯云COS文档预览支持的文件类型后缀,包括视频
export const COS_PREVIEWABLE_FILE_SUFFIXES_ADD_VIDEO = [
  ...PRESENTENTION_FILE_SUFFIXES,
  ...WORLD_FILE_SUFFIXES,
  ...EXCEL_FILE_SUFFIXES,
  ...OTHER_FILE_SUFFIXES,
  ...VIDEO_SUFFIXES,
  ...IMAGE_SUFFIXES,
];

// 浏览器直接能预览的文件类型后缀
export const BROWSER_PREVIEWABLE_FILE_SUFFIXES = [
  ...IMAGE_SUFFIXES,
  ...VIDEO_SUFFIXES,
];

// 可以预览的文件类型后缀，包含腾讯云COS预览插件、浏览器直接打开
export const PREVIEWABLE_FILE_SUFFIXES = [
  ...COS_PREVIEWABLE_FILE_SUFFIXES,
  ...BROWSER_PREVIEWABLE_FILE_SUFFIXES,
];

// 可以下载的附件类型后缀
export const DOWNLOADABLE_FILE_SUFFIXES = [
  // REMARK: 后面需要放开 附件下载功能时，解开下面的注释
  ...PRESENTENTION_FILE_SUFFIXES,
  ...WORLD_FILE_SUFFIXES,
  // ...EXCEL_FILE_SUFFIXES,
  ...COMMON_IMAGE_SUFFIXES,
  'pdf',
  'gif',
];

export const ALL_PLATFORMS_OPTION = {
  value: 'All platforms',
  text: 'All platforms',
};
export const PLATFORM_OPTIONS = [
  { value: 'Console', text: 'Console' },
  { value: 'Mobile', text: 'Mobile' },
  { value: 'PC', text: 'PC' },
];

export const STAGE_OPTIONS = [
  { value: 'Live', text: 'Live' },
  { value: 'Development', text: 'Development' },
  { value: 'Legacy', text: 'Legacy' },
  { value: 'Pre-planned', text: 'Pre-planned' },
  { value: 'Planned', text: 'Planned' },
  { value: 'Canceled', text: CONSTANT_TEXTS.CANCELED },
  { value: 'No Maintenance', text: 'No Maintenance' },
];

export const PRIORITY_OPTIONS = [
  { value: '11', text: 'S' },
  { value: '22', text: 'A' },
  { value: '33', text: 'B' },
  { value: '44', text: 'C' },
  { value: '99', text: 'Z' },
];

// 全局预定义gatereviews codes映射
export const StaticGatereviewDecisionsCodes = {
  Pass: '1',
  AwaitingApproval: '4',
  Redo: '2',
  Fail: '3',
  Empty: '0',
};

// 全局预定义gatereviews辅助信息配置
export const STATIC_GATEREVIEW_DECISIONS_NAMES = {
  [StaticGatereviewDecisionsCodes.Pass]: 'Pass',
  [StaticGatereviewDecisionsCodes.AwaitingApproval]: 'Pending Approval',
  [StaticGatereviewDecisionsCodes.Redo]: 'Redo',
  [StaticGatereviewDecisionsCodes.Fail]: 'Fail',
  [StaticGatereviewDecisionsCodes.Empty]: '-',
};

// 全局预定义gatereviews
export const STATIC_GATEREVIEW_DECISIONS = [
  {
    value: StaticGatereviewDecisionsCodes.Pass,
    text: STATIC_GATEREVIEW_DECISIONS_NAMES[
      StaticGatereviewDecisionsCodes.Pass
    ],
  },
  {
    value: StaticGatereviewDecisionsCodes.Fail,
    text: STATIC_GATEREVIEW_DECISIONS_NAMES[
      StaticGatereviewDecisionsCodes.Fail
    ],
  },
  {
    value: StaticGatereviewDecisionsCodes.Redo,
    text: STATIC_GATEREVIEW_DECISIONS_NAMES[
      StaticGatereviewDecisionsCodes.Redo
    ],
  },
  {
    value: StaticGatereviewDecisionsCodes.AwaitingApproval,
    text: STATIC_GATEREVIEW_DECISIONS_NAMES[
      StaticGatereviewDecisionsCodes.AwaitingApproval
    ],
  },
  {
    value: StaticGatereviewDecisionsCodes.Empty,
    text: STATIC_GATEREVIEW_DECISIONS_NAMES[
      StaticGatereviewDecisionsCodes.Empty
    ],
  },
];

// 全局预定义gatereviews codes集合
export const STATIC_GATEREVIEW_DECISIONS_CODES = [
  StaticGatereviewDecisionsCodes.Pass,
  StaticGatereviewDecisionsCodes.AwaitingApproval,
  StaticGatereviewDecisionsCodes.Redo,
  StaticGatereviewDecisionsCodes.Fail,
];

// 全局预定义GR Action Status
export const STATIC_GR_ACTION_STATUSES = [
  { value: 'Canceled', text: CONSTANT_TEXTS.CANCELED },
  { value: 'Completed', text: CONSTANT_TEXTS.COMPLETED },
  { value: 'In Progress', text: CONSTANT_TEXTS.IN_PROGRESS },
  { value: 'Not Started', text: CONSTANT_TEXTS.NOT_STARTED },
  { value: 'On Hold', text: CONSTANT_TEXTS.ON_HOLD },
];

export const StaticActionItemCodes = {
  NotStarted: '1',
  InProgress: '2',
  OnHold: '3',
  Completed: '4',
  Canceled: '5',
};

export const ACTION_ITEM_STATUS_MAPPING: IRecord<string> = {
  completed: StaticActionItemCodes.Completed,
  'not started': StaticActionItemCodes.NotStarted,
  'in progress': StaticActionItemCodes.InProgress,
  'on hold': StaticActionItemCodes.OnHold,
  canceled: StaticActionItemCodes.Canceled,
};

export const ACTION_ITEM_STATUS_LIST = [
  StaticActionItemCodes.Completed,
  StaticActionItemCodes.NotStarted,
  StaticActionItemCodes.InProgress,
  StaticActionItemCodes.OnHold,
  StaticActionItemCodes.Canceled,
];

export const ACTION_ITEM_STATUS_OPTIONS = [
  {
    text: CONSTANT_TEXTS.CANCELED,
    value: StaticActionItemCodes.Canceled,
  },
  {
    text: CONSTANT_TEXTS.COMPLETED,
    value: StaticActionItemCodes.Completed,
  },
  {
    text: CONSTANT_TEXTS.IN_PROGRESS,
    value: StaticActionItemCodes.InProgress,
  },
  {
    text: CONSTANT_TEXTS.NOT_STARTED,
    value: StaticActionItemCodes.NotStarted,
  },
  {
    text: CONSTANT_TEXTS.ON_HOLD,
    value: StaticActionItemCodes.OnHold,
  },
];

export const ACTION_ITEM_STATUS_NAMES_MAPPING: IRecord<string> = {
  [StaticActionItemCodes.NotStarted]: 'Not Started',
  [StaticActionItemCodes.InProgress]: 'In Progress',
  [StaticActionItemCodes.OnHold]: 'On Hold',
  [StaticActionItemCodes.Completed]: 'Completed',
  [StaticActionItemCodes.Canceled]: 'Canceled',
};

export const ACTION_ITEM_STATUS_NAMES_ENTRIES = Object.entries(
  ACTION_ITEM_STATUS_NAMES_MAPPING,
);

// 全局预定义GR Action Status 名称集合
export const STATIC_GR_ACTION_STATUS_NAMES: {
  [key: string]: string;
} = STATIC_GR_ACTION_STATUSES.reduce((acc, cur) => {
  const { value, text } = cur || {};
  return { ...acc, [value?.toLowerCase()]: text };
}, {});

// 全局预定义GR Action Status value集合
export const STATIC_GR_ACTION_STATUS_VALUES: {
  [key: string]: string;
} = STATIC_GR_ACTION_STATUSES.reduce((acc, cur) => {
  const { value, text } = cur || {};
  const textUpperCase = text?.toUpperCase() || '';
  return { ...acc, [textUpperCase]: value };
}, {});

export const CUSTOMIZED_OTHERS = {
  value: 'Customized Others',
  text: 'Customized Others',
};

// 全局预定义 Official Gate Review milestones
export const STATIC_OFFICIAL_GATE_REVIEW_MILESTONES = [
  { value: 'Concept', text: 'Concept' },
  {
    value: 'End of Pre-Production (Vertical Slice)',
    text: 'End of Pre-Production (Vertical Slice)',
  },
  { value: 'Feature Complete (Alpha)', text: 'Feature Complete (Alpha)' },
  { value: 'Content Complete (Beta)', text: 'Content Complete (Beta)' },
  // { value: 'Vertical Slice', text: 'Vertical Slice' },
  // { value: 'Alpha', text: 'Alpha' },
  // { value: 'Beta', text: 'Beta' },
  { value: 'Early Access', text: 'Early Access' },
];

// 全局预定义 Launch Schedule milestones
export const STATIC_LAUNCH_SCHEDULE_MILESTONES = [
  { value: 'Mobile Launch', text: 'Mobile Launch' },
  { value: 'PC Launch', text: 'PC Launch' },
  { value: 'Console Launch', text: 'Console Launch' },
];

// 全局预定义milestones
export const STATIC_MILESTONES = [
  { value: 'Pitch', text: 'Pitch' },
  { value: 'Concept', text: 'Concept' },
  { value: 'First Playable', text: 'First Playable' },
  ...STATIC_OFFICIAL_GATE_REVIEW_MILESTONES.slice(1),
  ...STATIC_LAUNCH_SCHEDULE_MILESTONES,
];

export const FORM_GROUPS: ISelectOptionWithGroup[] = [
  {
    value: CONSTANT_KEYS['1ST_PARTY'],
    label: CONSTANT_TEXTS['1ST_PARTY'],
    text: CONSTANT_TEXTS['1ST_PARTY'],
  },
  {
    value: CONSTANT_KEYS['2ND_PARTY'],
    label: CONSTANT_TEXTS['2ND_PARTY'],
    text: CONSTANT_TEXTS['2ND_PARTY'],
  },
  {
    value: CONSTANT_KEYS['3RD_PARTY'],
    label: CONSTANT_TEXTS['3RD_PARTY'],
    text: CONSTANT_TEXTS['3RD_PARTY'],
  },
];

export const SUB_FORM_GROUPS: IRecord<ISelectOptionWithGroup[]> = {
  [CONSTANT_KEYS['1ST_PARTY']]: [
    { value: 'NExT', label: 'NExT', text: 'NExT' },
    { value: 'NExT-BJ Leyou', label: 'NExT-BJ Leyou', text: 'NExT-BJ Leyou' },
  ],
  [CONSTANT_KEYS['2ND_PARTY']]: [
    {
      value: 'Project financing',
      label: 'Project financing',
      text: 'Project financing',
    },
    { value: 'Project loan', label: 'Project loan', text: 'Project loan' },
    { value: 'Self-funded', label: 'Self-funded', text: 'Self-funded' },
    { value: 'Work for hire', label: 'Work for hire', text: 'Work for hire' },
  ],
  [CONSTANT_KEYS['3RD_PARTY']]: [
    { value: 'Co-dev', label: 'Co-dev', text: 'Co-dev' },
    {
      value: 'Project financing',
      label: 'Project financing',
      text: 'Project financing',
    },
  ],
};

export const FIELD_COMMON_ERR_MSG = 'Please complete this mandatory field';

export const NAME_ALREADY_ERR_MSG = 'The same name already exists.';

// 默认为GR的milestones集合，只是修改时默认打开gr开关，用户可以自己关闭gr开关
export const DEFAULT_GATE_REVIEW_MILESTONES = [
  'Concept',
  'Concept/KOM',
  'Feature Complete (Alpha)',
  'End of Pre-Production (Vertical Slice)',
  'Content Complete (Beta)',
];
// export const DEFAULT_GATE_REVIEW_MILESTONES = ['Concept', 'Concept/KOM', 'Alpha', 'Vertical Slice', 'Beta'];

// 默认Launch的milestones集合，只是修改时默认打开launch开关，用户可以自己关闭launch开关
export const DEFAULT_LAUNCH_MILESTONES = [
  'Early Access',
  'Mobile Launch',
  'PC Launch',
  'Console Launch',
];

/**
 * milestone名称纠错映射
 * 键：错误的名字（大小写错误）
 * 值：正确的名字
 */
export const MILESTONE_CORRECT_NAMES: IRecord<string> = {
  'Soft launch': 'Soft Launch',
};

/**
 * milestone别名映射
 * 键: 别名、曾用名
 * 值: 最新名称
 */
export const MILESTONE_NAME_ALIAS: IRecord<string> = {
  'Concept/KOM': 'Concept',
};

// 搜索历史的type配置
export const HISTORY_PAGE_TYPES = {
  // Pipeline/Project List页面宽表搜索
  PROJECT_LIST: 1,
  // Workbench页面宽表搜索（新风格该页面已下架了）
  WORKBENCH: 2,
  // All Studio Overview页面宽表搜索
  STUDIO_OVERVIEW: 4,
  // 暂时已没用到，但是历史数据中有5，所以5不能用于别的场景了
  PIPELINE_PROJECT_DETAILS_HEADER: 5,
  // Project详情页Visual Showcase模块搜索
  PIPELINE_PROJECT_DETAILS_VISUAL_SHOWCASE: 6,
  // Pipeline/GR Action Items页面宽表搜索
  PIPELINE_GR_ACTION_ITEMS: 7,
  // Pipeline / Change Log / Change Detail页面宽表搜索
  PIPELINE_CHANGE_LOG_CHANGE_DETAIL: 8,
  // Pipeline / Change Log / Change Checklist页面宽表搜索
  PIPELINE_CHANGE_LOG_CHANGE_CHECKLIST: 11,
  // Pipeline / Project详情页
  PIPELINE_PROJECT_DETAILS: 9,
  // Metrics指标说明页
  METRICS: 10,
  // Pipeline/Overview页面宽表搜索
  OVERVIEW: 11,
};

export const DEFAULT_PAGINATION: PagingQuery = {
  pageIndex: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const DEFAULT_SEARCH: ISearchPayloadPageStore = {
  search_type: 'exact',
  search_keyword: '',
  search: [],
  search_ids: [],
  search_list: [],
};

// upload-button-tips
export const UPLOAD_BUTTON_TIPS =
  'Batch upload supported. File formats accepted: doc, docx, pdf, ppt, pptx, csv, xlsx, jpg, jpeg, png, bmp, webp, mp4, mov. The video size must not exceed 5GB. ';
export const UPLOAD_BUTTON_OVERVIEW_INFO_TIPS =
  'Only single file upload is supported. File formats accepted: doc, docx, pdf, ppt, pptx, csv, xlsx, jpg, jpeg, png, bmp, webp. ';
export const UPLOAD_BUTTON_VISUAL_INFO_TIPS =
  'video size cannot exceed 5GB, and the system will automatically generate a cover image with a minimum resolution of 1900*600 pixels. Screenshot size cannot exceed 500MB. ';

export const MILESTONE_CATEGORY_LIST = [
  {
    value: false,
    key: 'gr',
    text: 'GR',
    icon: 'flag',
  },
  {
    value: false,
    key: 'check_in',
    text: 'Check in',
    icon: 'rectangle',
  },
  { value: false, key: 'launch', text: 'Launch' },
  { value: false, key: 'test', text: 'Test' },
  { value: false, key: 'others', text: 'Others' },
];

export const CATEGORY_TYPE = ['gr', 'check_in', 'launch', 'test', 'others'];

export const MILESTONE_CATEGORY_LIST_OPTIONS = [
  {
    value: 'gr',
    text: 'GR',
  },
  {
    value: 'check_in',
    text: 'Check in',
  },
  { value: 'launch', text: 'Launch' },
  { value: 'test', text: 'Test' },
  { value: 'others', text: 'Others' },
];

export const MILESTONE_KEY_LIST = ['gr', 'check_in', 'launch'];

export const MILESTONE_CATEGORY_MAP = {
  gr: 'GR',
  check_in: 'Check in',
  launch: 'Launch',
  test: 'Test',
  others: 'Others',
};

export const DBG_DETAILS_CUR_MILESTONE_ID = '__dbg_detail_cur_milestone_id__';
export const DBG_SHOW_FOLLOW_TIPS_LOCAL_KEY =
  '__dbg_show_follow_tips_local_key__';

export const UPLOAD_FOLDER_NAME_MAP = {
  gameInfo: 'project_overview_mandate',
  milestoneReview: 'gate_review_material',
  updatesMaterial: 'project_updates_material',
  studioMeetingFile: 'meeting_file',
  image: 'image',
  visualFile: 'visual_showcase',
};
// cos旧桶key
export const COS_BUCKET_OLD = 'databrain-gp-board-1300342648';
// cos新桶key
export const COS_BUCKET_NEW = 'databrain-gp-mgmt-1395457873';

// GPOP新手指引文档链接
export const GPOP_BEGINNER_GUIDE_DOC_URL =
  'https://doc.weixin.qq.com/doc/w3_ABQA3AY2AD4O1naFnvDTxaOLvFAuO';
